import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        toast.success('Accesso effettuato con successo!');
      } else {
        await register(email, password, name);
        toast.success('Registrazione completata!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'autenticazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-12 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img src="/logo-512.png" alt="360 Community" className="h-16 w-16 rounded-xl" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">
              Lead Manager
            </h1>
            <p className="text-slate-600">
              {isLogin ? 'Accedi al tuo account' : 'Crea un nuovo account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-2 block">
                  Nome
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={!isLogin}
                  className="h-11"
                  data-testid="name-input"
                  placeholder="Mario Rossi"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-2 block">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
                placeholder="mario@esempio.it"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-2 block">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                data-testid="password-input"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm hover:shadow-indigo-200/50 rounded-lg font-semibold transition-all active:scale-95"
              data-testid="submit-button"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Caricamento...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  <span>{isLogin ? 'Accedi' : 'Registrati'}</span>
                </div>
              )}
            </Button>
          </form>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Non hai un account? Registrati' : 'Hai già un account? Accedi'}
            </button>
          </div>
        </div>
      </div>

      <div
        className="hidden lg:flex w-1/2 bg-slate-900 relative overflow-hidden items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-slate-900/75 to-slate-900/80"></div>
        <div className="relative z-10 text-center text-white px-12 max-w-xl">
          {/* Avatar circolari di persone */}
          <div className="flex justify-center mb-8 -space-x-4">
            <img 
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face" 
              alt="Team member" 
              className="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=face" 
              alt="Team member" 
              className="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face" 
              alt="Team member" 
              className="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face" 
              alt="Team member" 
              className="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
            />
            <img 
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" 
              alt="Team member" 
              className="w-14 h-14 rounded-full border-3 border-white shadow-lg object-cover"
            />
          </div>
          <h2 className="text-5xl font-bold mb-6 tracking-tight">
            Gestisci i tuoi lead con efficienza
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            Assegnazione automatica, pipeline visiva e tracking completo per massimizzare le conversioni.
          </p>
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-400">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              +500 lead gestiti
            </span>
            <span>•</span>
            <span>Team sempre connesso</span>
          </div>
        </div>
      </div>
    </div>
  );
}